if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      connectedCallback() {
        if (this.abortController || !window.ScoreggiaMediaCore?.CircularCarousel) return;
        this.mediaReady = false;
        this.mediaReadyOwnership = null;
        this.abortController = new AbortController();
        this.liveRegion = this.querySelector('[id^="GalleryStatus"]');
        this.viewer = this.querySelector('[id^="GalleryViewer"]');
        this.viewport = this.querySelector('[data-scoreggia-product-viewport]');
        this.zoomMinimumScale = 1;
        this.zoomMaximumScale = 5;
        this.zoomInitialScale = window.ScoreggiaMediaCore.clampScale(
          Number.parseFloat(this.dataset.scoreggiaZoomLevel || '2'),
          this.zoomMinimumScale,
          this.zoomMaximumScale,
        );
        this.zoomGestureOutcome = window.ScoreggiaMediaCore.zoomGestureOutcome;
        this.zoomScale = this.zoomInitialScale;
        this.zoomed = false;
        this.panX = 0;
        this.panY = 0;
        this.zoomRenderedPanX = 0;
        this.zoomRenderedPanY = 0;
        this.zoomBounds = null;
        this.zoomCenteredBounds = null;
        this.zoomSourceBounds = null;
        this.zoomSourceSlide = null;
        this.zoomSourceMediaId = null;
        this.zoomReturnMode = null;
        this.zoomReturnProgress = 0;
        this.zoomPointers = new Map();
        this.zoomMode = null;
        this.zoomPanStart = null;
        this.zoomPinchStart = null;
        this.zoomPinchPointerIds = [];
        this.zoomManipulated = false;
        this.activeZoomPointerListeners = null;
        this.zoomSourceImage = null;
        this.zoomSourceButton = null;
        this.suppressNextZoomClick = false;
        this.zoomCloseTimer = null;
        this.zoomClickTimer = null;
        this.zoomAnnouncementTimer = null;
        this.zoomOpenFrame = null;
        this.zoomTransformFrame = null;
        this.zoomWheelFrame = null;
        this.zoomWheelIdleTimer = null;
        this.zoomWheelSamples = [];
        this.productLayoutFrame = null;
        this.productLayoutResizeObserver = null;
        this.quickAdd = Boolean(this.closest('quick-add-modal'));
        this.productPage =
          this.dataset.scoreggiaProductPage === 'true' &&
          !this.quickAdd &&
          Boolean(document.querySelector('main[data-template="product"]')?.contains(this));
        this.zoomEnabled = this.productPage && this.dataset.scoreggiaInlineZoom === 'true';
        this.mediaStateSequence = 0;

        this.controller = new window.ScoreggiaMediaCore.CircularCarousel(this, {
          trackSelector: '[data-scoreggia-product-track]',
          viewportSelector: '[data-scoreggia-product-viewport]',
          slideSelector: '[data-scoreggia-product-slide]',
          thumbnailSelector: '[data-scoreggia-product-thumbnail]',
          previousSelector: '[data-scoreggia-product-previous]',
          nextSelector: '[data-scoreggia-product-next]',
          gestureSurfaceSelector: '[data-scoreggia-zoom]:not([hidden]):not([disabled])',
          beforeChange: (nextIndex, previousIndex) => {
            if (nextIndex !== previousIndex) this.resetZoom({ immediate: true });
          },
          afterChange: (nextIndex, previousIndex, options) => this.handleMediaChange(nextIndex, previousIndex, options),
          isZoomed: () => this.zoomed,
          onEscape: () => {
            if (!this.zoomed) return false;
            this.resetZoom({ restoreFocus: true });
            return true;
          },
        });
        this.controller.connect();
        this.configureZoomControls();
        if (this.zoomEnabled) this.ensureZoomLayer();
        this.bindEvents();
        this.configureProductLayout();
        this.markVariantMediaReady();
      }

      disconnectedCallback() {
        this.mediaReady = false;
        this.mediaReadyOwnership = null;
        this.mediaStateSequence += 1;
        this.clearProductLayout();
        this.resetZoom({ immediate: true });
        this.removeZoomLayer();
        this.controller?.disconnect();
        this.controller = null;
        this.abortController?.abort();
        this.abortController = null;
      }

      bindEvents() {
        const { signal } = this.abortController;
        this.addEventListener(
          'click',
          (event) => {
            const zoomButton = event.target.closest('[data-scoreggia-zoom]');
            if (zoomButton && this.zoomEnabled && !zoomButton.hidden) {
              event.preventDefault();
              this.setZoom(!this.zoomed);
            }
          },
          { signal },
        );
        document.addEventListener(
          'keydown',
          (event) => {
            if (event.key !== 'Escape' || !this.zoomed) return;
            event.preventDefault();
            this.resetZoom({ restoreFocus: true });
          },
          { signal },
        );
        window.addEventListener(
          'resize',
          () => this.scheduleViewportGeometryRefresh(),
          { signal },
        );
        window.addEventListener('orientationchange', () => this.scheduleViewportGeometryRefresh(), { signal });
        window.addEventListener('orientationchange', () => this.refreshZoomBounds(), { signal });
        window.visualViewport?.addEventListener('resize', () => this.scheduleViewportGeometryRefresh(), { signal });
        window.visualViewport?.addEventListener('resize', () => this.refreshZoomBounds(), { signal });
        window.visualViewport?.addEventListener('scroll',
          () => {
            if (this.zoomed) this.scheduleViewportGeometryRefresh();
          },
          { signal },
        );
        window.addEventListener('pagehide', () => this.resetZoom({ immediate: true }), { signal });
      }

      configureProductLayout() {
        if (!this.productPage || !this.viewport) return;
        this.productInfo = this.closest('product-info');
        this.scheduleViewportGeometryRefresh();
        if (!('ResizeObserver' in window)) return;
        this.productLayoutResizeObserver = new ResizeObserver(() => this.scheduleViewportGeometryRefresh());
        this.productLayoutResizeObserver.observe(this.closest('.product__media-wrapper') || this);
      }

      clearProductLayout() {
        if (this.productLayoutFrame !== null) window.cancelAnimationFrame(this.productLayoutFrame);
        this.productLayoutFrame = null;
        this.productLayoutResizeObserver?.disconnect();
        this.productLayoutResizeObserver = null;
        this.productInfo?.removeAttribute('data-scoreggia-viewport-mode');
        this.productInfo?.style?.removeProperty('--scoreggia-pdp-usable-height');
        this.productInfo?.style?.removeProperty('--scoreggia-pdp-usable-width');
        this.style.removeProperty('--scoreggia-pdp-stage-height');
        this.style.removeProperty('--scoreggia-pdp-media-aspect-ratio');
      }

      scheduleViewportGeometryRefresh() {
        if (this.productLayoutFrame !== null) return;
        this.productLayoutFrame = window.requestAnimationFrame(() => {
          this.productLayoutFrame = null;
          if (this.zoomed) this.refreshZoomBounds();
          this.updateProductLayout();
        });
      }

      updateProductLayout() {
        if (!this.productPage || !this.viewport?.isConnected) return;
        const visualViewport = window.visualViewport;
        const usableHeight = Math.max(
          1,
          Number(visualViewport?.height) || Number(window.innerHeight) || document.documentElement.clientHeight || 1,
        );
        const usableWidth = Math.max(
          1,
          Number(visualViewport?.width) || Number(window.innerWidth) || document.documentElement.clientWidth || 1,
        );
        const viewportBounds = this.viewport.getBoundingClientRect();
        const stageWidth = Math.max(1, viewportBounds.width || this.viewport.clientWidth || this.clientWidth || 1);
        const visualOffsetTop = Number(visualViewport?.offsetTop) || 0;
        const layoutScrollTop = Math.max(
          0,
          Number(window.scrollY) || Number(window.pageYOffset) || Number(document.documentElement.scrollTop) || 0,
        );
        const stageTop = Math.max(0, viewportBounds.top + layoutScrollTop - visualOffsetTop);
        const visibleStageTop = stageTop < usableHeight ? stageTop : 0;
        const mediaAspectRatio = this.activeMediaAspectRatio();
        const naturalStageHeight = stageWidth / mediaAspectRatio;
        const viewportAspectRatio = usableWidth / usableHeight;
        const landscape = usableWidth > usableHeight;
        const shortLandscape =
          landscape &&
          (usableHeight <= 560 || (usableHeight <= 640 && usableWidth < 1200 && viewportAspectRatio >= 1.35));
        const portrait = usableHeight >= usableWidth;
        const heightFraction = shortLandscape ? 0.9 : portrait && usableWidth < 750 ? 0.68 : 0.76;
        const topAwareHeight = shortLandscape ? usableHeight - visibleStageTop - 12 : usableHeight * heightFraction;
        const stageHeight = Math.max(1, Math.min(naturalStageHeight, topAwareHeight, 720));
        const viewportMode = shortLandscape
          ? 'short-landscape'
          : portrait && usableWidth < 750
            ? 'tall-mobile'
            : portrait && usableWidth < 1200
              ? 'tablet-portrait'
              : 'adequate-height';

        this.style.setProperty('--scoreggia-pdp-stage-height', `${stageHeight.toFixed(2)}px`);
        this.style.setProperty('--scoreggia-pdp-media-aspect-ratio', mediaAspectRatio.toFixed(6));
        this.productInfo?.style?.setProperty('--scoreggia-pdp-usable-height', `${usableHeight.toFixed(2)}px`);
        this.productInfo?.style?.setProperty('--scoreggia-pdp-usable-width', `${usableWidth.toFixed(2)}px`);
        if (this.productInfo) this.productInfo.dataset.scoreggiaViewportMode = viewportMode;
      }

      activeMediaAspectRatio() {
        const activeSlide = this.controller?.slides?.[this.controller.activeIndex];
        const image = activeSlide?.querySelector('img');
        const width = Number(image?.getAttribute('width')) || Number(image?.naturalWidth);
        const height = Number(image?.getAttribute('height')) || Number(image?.naturalHeight);
        if (width > 0 && height > 0) return width / height;

        const container = activeSlide?.querySelector('.product-media-container');
        const ratio = Number.parseFloat(container?.style?.getPropertyValue('--preview-ratio'));
        return Number.isFinite(ratio) && ratio > 0 ? ratio : 1;
      }

      handleMediaChange(nextIndex, previousIndex, options) {
        if (nextIndex !== previousIndex) this.mediaStateSequence += 1;
        this.pauseInactiveMedia(nextIndex);
        const activeSlide = this.controller.slides[nextIndex];
        this.viewer?.dispatchEvent(
          new CustomEvent('slideChanged', {
            detail: { currentElement: activeSlide },
          }),
        );
        if (options.source !== 'initialize' && activeSlide) this.announceMedia(activeSlide, nextIndex + 1);
        this.scheduleViewportGeometryRefresh();
      }

      pauseInactiveMedia(activeIndex) {
        this.controller.slides.forEach((slide, index) => {
          if (index === activeIndex) return;
          slide.querySelectorAll('video').forEach((video) => video.pause());
          slide.querySelectorAll('.js-youtube').forEach((video) => {
            video.contentWindow?.postMessage('{"event":"command","func":"pauseVideo","args":""}', '*');
          });
          slide.querySelectorAll('.js-vimeo').forEach((video) => {
            video.contentWindow?.postMessage('{"method":"pause"}', '*');
          });
          slide.querySelectorAll('product-model').forEach((model) => model.modelViewerUI?.pause?.());
        });
      }

      announceMedia(activeSlide, position) {
        if (!this.liveRegion) return;
        const image = activeSlide.querySelector('img');
        const announce = () => {
          this.liveRegion.setAttribute('aria-hidden', 'false');
          this.liveRegion.textContent = window.accessibilityStrings.imageAvailable.replace('[index]', position);
          window.setTimeout(() => this.liveRegion?.setAttribute('aria-hidden', 'true'), 2000);
        };
        if (!image || image.complete) announce();
        else image.addEventListener('load', announce, { once: true });
      }

      currentMediaId() {
        return this.controller?.slides?.[this.controller.activeIndex]?.dataset.mediaId || null;
      }

      mediaIndexIsCurrent() {
        if (!this.controller) return false;
        const renderedSlides = Array.from(this.querySelectorAll('[data-scoreggia-product-slide]'));
        const renderedThumbnails = Array.from(this.querySelectorAll('[data-scoreggia-product-thumbnail]'));
        return Boolean(
          renderedSlides.length &&
          this.controller.slides.length === renderedSlides.length &&
          this.controller.slides.every((slide, index) => slide === renderedSlides[index]) &&
          this.controller.thumbnails.length === renderedThumbnails.length &&
          this.controller.thumbnails.every((thumbnail, index) => thumbnail === renderedThumbnails[index])
        );
      }

      markVariantMediaReady() {
        if (
          !this.isConnected ||
          !this.abortController ||
          !this.controller?.abortController ||
          !this.mediaIndexIsCurrent()
        ) {
          this.mediaReady = false;
          this.mediaReadyOwnership = null;
          return false;
        }

        this.mediaReady = true;
        this.mediaReadyOwnership = this.closest('product-info');
        this.dispatchEvent(
          new CustomEvent('media-gallery:ready', {
            bubbles: true,
            detail: {
              productId: String(this.mediaReadyOwnership?.dataset.productId || ''),
              root: this.mediaReadyOwnership,
              sectionId: String(this.mediaReadyOwnership?.dataset.section || ''),
            },
          }),
        );
        return true;
      }

      setActiveMedia(mediaId) {
        if (!this.controller || this.mediaReady === false) return false;
        const normalizedMediaId = String(mediaId || '');
        const renderedSlides = Array.from(this.querySelectorAll('[data-scoreggia-product-slide]'));
        if (!renderedSlides.some((slide) => slide.dataset.mediaId === normalizedMediaId)) return false;

        const controllerSlides = this.controller?.slides || [];
        const controllerIsCurrent =
          controllerSlides.length === renderedSlides.length &&
          controllerSlides.every((slide, index) => slide === renderedSlides[index]);
        const currentMediaId = controllerSlides[this.controller?.activeIndex]?.dataset.mediaId;
        if (controllerIsCurrent && currentMediaId === normalizedMediaId) {
          this.scheduleViewportGeometryRefresh();
          return true;
        }

        if (!controllerIsCurrent) this.controller.refresh();
        const nextIndex = this.controller.slides.findIndex((slide) => slide.dataset.mediaId === normalizedMediaId);
        if (nextIndex < 0) return false;
        if (nextIndex === this.controller.activeIndex) {
          this.scheduleViewportGeometryRefresh();
          return true;
        }

        this.resetZoom({ immediate: true });
        this.controller.goTo(nextIndex, { immediate: true, source: 'variant' });
        this.preventStickyHeader();
        return true;
      }

      prepareForMediaMutation() {
        this.mediaStateSequence += 1;
        this.mediaReady = false;
        this.mediaReadyOwnership = null;
        this.resetZoom({ immediate: true });
        this.controller?.prepareForMutation();
      }

      completeMediaMutation() {
        if (!this.controller) return false;
        this.controller.refresh();
        return this.markVariantMediaReady();
      }

      setActiveThumbnail(thumbnail) {
        if (!thumbnail) return;
        const target = thumbnail.dataset.target;
        const nextIndex = this.controller.slides.findIndex((slide) => slide.dataset.mediaId === target);
        if (nextIndex >= 0) this.controller.goTo(nextIndex, { source: 'thumbnail' });
      }

      preventStickyHeader() {
        this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
        this.stickyHeader?.dispatchEvent(new Event('preventHeaderReveal'));
      }

      configureZoomControls() {
        this.querySelectorAll('[data-scoreggia-zoom]').forEach((button) => {
          if (this.zoomEnabled) return;
          button.hidden = true;
          button.disabled = true;
          button.setAttribute('aria-hidden', 'true');
        });
      }

      ensureZoomLayer() {
        if (this.zoomRoot?.isConnected) return;
        const staleRoot = document.querySelector('[data-scoreggia-elevated-zoom]');
        if (staleRoot) staleRoot.remove();

        const root = document.createElement('div');
        const dismissalLayer = document.createElement('div');
        const image = document.createElement('img');
        root.className = 'scoreggia-elevated-zoom';
        root.dataset.scoreggiaElevatedZoom = '';
        root.hidden = true;
        dismissalLayer.className = 'scoreggia-elevated-zoom__dismissal';
        dismissalLayer.dataset.scoreggiaZoomDismissal = '';
        dismissalLayer.setAttribute('aria-hidden', 'true');
        image.className = 'scoreggia-elevated-zoom__image';
        image.dataset.scoreggiaZoomImage = '';
        image.alt = '';
        image.draggable = false;
        image.setAttribute('aria-hidden', 'true');
        root.append(dismissalLayer, image);
        document.body.append(root);
        this.zoomRoot = root;
        this.zoomDismissalLayer = dismissalLayer;
        this.zoomImage = image;

        const { signal } = this.abortController;
        dismissalLayer.addEventListener('click', (event) => this.handleZoomDismissal(event), { signal });
        image.addEventListener('pointerdown', (event) => this.handleZoomPointerDown(event), { signal });
        image.addEventListener('wheel', (event) => this.handleZoomWheel(event), { passive: false, signal });
        image.addEventListener('lostpointercapture', (event) => {
          const pointer = this.zoomPointers.get(event.pointerId);
          if (!pointer?.captured) return;
          pointer.captured = false;
          this.finishZoomPointer(event.pointerId, true);
        }, { signal });
        image.addEventListener('click', (event) => this.handleZoomImageClick(event), { signal });
        image.addEventListener('load', () => {
          if (this.zoomed) this.refreshZoomBounds();
        }, { signal });
        image.addEventListener('error', () => {
          if (this.zoomed) this.resetZoom({ immediate: true, restoreFocus: true });
        }, { signal });
        image.addEventListener('dragstart', (event) => event.preventDefault(), { signal });
      }

      removeZoomLayer() {
        this.finishZoomClose();
        this.zoomRoot?.remove();
        this.zoomRoot = null;
        this.zoomDismissalLayer = null;
        this.zoomImage = null;
      }

      setZoom(enabled) {
        if (!this.zoomEnabled) return;
        const activeSlide = this.controller.slides[this.controller.activeIndex];
        const zoomButton = activeSlide?.querySelector('[data-scoreggia-zoom]');
        const zoomImage = activeSlide?.querySelector('.scoreggia-inline-zoom__image');
        if (!zoomButton || !zoomImage || activeSlide?.hasAttribute('data-scoreggia-loop-clone')) return;

        this.resetZoom({ immediate: true });
        if (!enabled) return;
        this.ensureZoomLayer();
        document.documentElement.classList.add('scoreggia-elevated-zoom-open');
        const renderedBounds = this.getRenderedImageBounds(zoomImage);
        const centeredBounds = this.getCenteredZoomBounds(renderedBounds);
        if (!renderedBounds || !centeredBounds || !this.zoomRoot || !this.zoomImage) {
          document.documentElement.classList.remove('scoreggia-elevated-zoom-open');
          return;
        }
        this.zoomed = true;
        this.zoomScale = this.zoomInitialScale;
        this.panX = 0;
        this.panY = 0;
        this.zoomRenderedPanX = 0;
        this.zoomRenderedPanY = 0;
        this.zoomSourceBounds = renderedBounds;
        this.zoomCenteredBounds = centeredBounds;
        this.zoomBounds = centeredBounds;
        this.zoomSourceSlide = activeSlide;
        this.zoomSourceMediaId = activeSlide.dataset.mediaId || null;
        this.zoomReturnMode = null;
        this.zoomReturnProgress = 0;
        this.zoomSourceImage = zoomImage;
        this.zoomSourceButton = zoomButton;
        this.classList.add('is-zoomed');
        activeSlide.classList.add('is-zoomed');
        zoomButton.setAttribute('aria-pressed', 'true');
        zoomButton.setAttribute('aria-label', zoomButton.dataset.exitLabel);
        zoomButton.querySelector('.visually-hidden').textContent = zoomButton.dataset.exitLabel;
        this.zoomImage.src = zoomImage.currentSrc || zoomImage.getAttribute('src');
        this.zoomImage.width = zoomImage.naturalWidth || Math.round(this.zoomBounds.width);
        this.zoomImage.height = zoomImage.naturalHeight || Math.round(this.zoomBounds.height);
        this.updateZoomImageLayout();
        this.zoomRoot.hidden = false;
        this.zoomRoot.classList.remove('is-active', 'is-closing');
        this.applyZoomTransform({ immediate: true });
        zoomImage.classList.add('is-scoreggia-elevated-zoom-source');
        this.announceZoomState(zoomButton.dataset.exitLabel);
        this.zoomOpenFrame = window.requestAnimationFrame(() => {
          this.zoomOpenFrame = null;
          if (this.zoomed) this.zoomRoot?.classList.add('is-active');
        });
      }

      getRenderedImageBounds(image) {
        const bounds = image?.getBoundingClientRect();
        if (!bounds?.width || !bounds?.height) return null;
        const computed = getComputedStyle(image);
        return window.ScoreggiaMediaCore.containedImageBounds({
          bounds: { left: bounds.left, top: bounds.top, width: bounds.width, height: bounds.height },
          naturalWidth: image.naturalWidth,
          naturalHeight: image.naturalHeight,
          objectFit: computed.objectFit,
          objectPosition: computed.objectPosition,
        });
      }

      getCenteredZoomBounds(bounds) {
        if (!bounds) return null;
        const viewport = this.getZoomViewport();
        return window.ScoreggiaMediaCore.centerBoundsInViewport({
          bounds,
          viewportWidth: viewport.width,
          viewportHeight: viewport.height,
          viewportLeft: viewport.left,
          viewportTop: viewport.top,
        });
      }

      getZoomViewport() {
        const viewport = window.visualViewport;
        return {
          width: Math.max(viewport?.width || window.innerWidth || 0, 1),
          height: Math.max(viewport?.height || window.innerHeight || 0, 1),
          left: viewport?.offsetLeft || 0,
          top: viewport?.offsetTop || 0,
        };
      }

      updateZoomImageLayout() {
        if (!this.zoomImage || !this.zoomBounds) return;
        this.zoomImage.style.left = `${this.zoomBounds.left}px`;
        this.zoomImage.style.top = `${this.zoomBounds.top}px`;
        this.zoomImage.style.width = `${this.zoomBounds.width}px`;
        this.zoomImage.style.height = `${this.zoomBounds.height}px`;
      }

      zoomSourceIsCurrent() {
        if (!this.zoomSourceImage?.isConnected) return false;
        if (!this.zoomSourceSlide) return true;
        const activeSlide = this.controller?.slides?.[this.controller.activeIndex];
        return (
          this.zoomSourceSlide.isConnected &&
          activeSlide === this.zoomSourceSlide &&
          !this.zoomSourceSlide.hasAttribute('data-scoreggia-loop-clone') &&
          (!this.zoomSourceMediaId || this.zoomSourceSlide.dataset.mediaId === this.zoomSourceMediaId) &&
          (!this.zoomSourceSlide.contains || this.zoomSourceSlide.contains(this.zoomSourceImage))
        );
      }

      refreshZoomSourceGeometry({ resetOnFailure = true } = {}) {
        if (!this.zoomSourceIsCurrent()) {
          if (resetOnFailure) this.resetZoom({ immediate: true, restoreFocus: true });
          return false;
        }
        const sourceBounds = this.getRenderedImageBounds(this.zoomSourceImage);
        const centeredBounds = this.getCenteredZoomBounds(sourceBounds);
        if (
          !sourceBounds ||
          !centeredBounds ||
          ![
            sourceBounds.left,
            sourceBounds.top,
            sourceBounds.width,
            sourceBounds.height,
            centeredBounds.left,
            centeredBounds.top,
          ].every(Number.isFinite)
        ) {
          if (resetOnFailure) this.resetZoom({ immediate: true, restoreFocus: true });
          return false;
        }
        this.zoomSourceBounds = sourceBounds;
        this.zoomCenteredBounds = centeredBounds;
        return true;
      }

      refreshZoomBounds() {
        if (this.productLayoutFrame !== null && this.productLayoutFrame !== undefined) return;
        if (!this.zoomed) return;
        if (!this.refreshZoomSourceGeometry()) return;
        this.applyZoomTransform({ immediate: true });
        this.restartZoomManipulation();
      }

      announceZoomState(message) {
        if (!this.liveRegion || !message) return;
        if (this.zoomAnnouncementTimer !== null) window.clearTimeout(this.zoomAnnouncementTimer);
        this.liveRegion.setAttribute('aria-hidden', 'false');
        this.liveRegion.textContent = message;
        this.zoomAnnouncementTimer = window.setTimeout(() => {
          this.liveRegion?.setAttribute('aria-hidden', 'true');
          this.zoomAnnouncementTimer = null;
        }, 2000);
      }

      resetZoom({ immediate = false, restoreFocus = false } = {}) {
        if (this.zoomCloseTimer !== null) {
          window.clearTimeout(this.zoomCloseTimer);
          this.zoomCloseTimer = null;
        }
        if (this.zoomOpenFrame !== null) {
          window.cancelAnimationFrame(this.zoomOpenFrame);
          this.zoomOpenFrame = null;
        }
        this.clearZoomClickSuppression();
        this.cancelZoomManipulation();
        this.clearZoomWheelState();
        this.clearZoomTransformFrame();
        const layerWasVisible = Boolean(this.zoomRoot && !this.zoomRoot.hidden);
        this.zoomed = false;
        this.zoomScale = this.zoomInitialScale;
        this.panX = 0;
        this.panY = 0;
        this.zoomRenderedPanX = 0;
        this.zoomRenderedPanY = 0;
        this.zoomReturnMode = null;
        this.zoomReturnProgress = 0;
        this.classList.remove('is-zoomed');
        document.documentElement.classList.remove('scoreggia-elevated-zoom-open');
        this.querySelectorAll('.scoreggia-product-media__slide.is-zoomed').forEach((slide) => {
          slide.classList.remove('is-zoomed');
        });
        this.querySelectorAll('[data-scoreggia-zoom]').forEach((button) => {
          button.setAttribute('aria-pressed', 'false');
          button.setAttribute('aria-label', button.dataset.zoomLabel);
          const label = button.querySelector('.visually-hidden');
          if (label) label.textContent = button.dataset.zoomLabel;
        });
        this.zoomRoot?.classList.remove('is-active');
        this.zoomRoot?.classList.toggle('is-closing', layerWasVisible && !immediate);

        if (!layerWasVisible || immediate || window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
          this.finishZoomClose({ restoreFocus });
          return;
        }
        this.zoomCloseTimer = window.setTimeout(() => this.finishZoomClose({ restoreFocus }), 140);
      }

      finishZoomClose({ restoreFocus = false } = {}) {
        if (this.zoomCloseTimer !== null) window.clearTimeout(this.zoomCloseTimer);
        this.zoomCloseTimer = null;
        if (this.zoomAnnouncementTimer !== null) window.clearTimeout(this.zoomAnnouncementTimer);
        this.zoomAnnouncementTimer = null;
        this.zoomRoot?.classList.remove('is-active', 'is-closing');
        if (this.zoomRoot) this.zoomRoot.hidden = true;
        if (this.zoomImage) {
          this.zoomImage.removeAttribute('src');
          this.zoomImage.removeAttribute('style');
        }
        this.zoomSourceImage?.classList.remove('is-scoreggia-elevated-zoom-source');
        const focusTarget = this.zoomSourceButton;
        this.zoomSourceImage = null;
        this.zoomSourceButton = null;
        this.zoomSourceSlide = null;
        this.zoomSourceMediaId = null;
        this.zoomSourceBounds = null;
        this.zoomCenteredBounds = null;
        this.zoomBounds = null;
        this.zoomScale = this.zoomInitialScale;
        this.panX = 0;
        this.panY = 0;
        this.zoomRenderedPanX = 0;
        this.zoomRenderedPanY = 0;
        this.zoomReturnMode = null;
        this.zoomReturnProgress = 0;
        if (restoreFocus && focusTarget?.isConnected) focusTarget.focus({ preventScroll: true });
      }

      applyZoomTransform({ immediate = false } = {}) {
        if (!this.zoomImage || !this.zoomed) return;
        this.zoomScale = window.ScoreggiaMediaCore.clampScale(
          this.zoomScale,
          this.zoomMinimumScale,
          this.zoomMaximumScale,
        );
        const viewport = this.getZoomViewport();
        const desktopReturn =
          this.zoomReturnMode === 'desktop-wheel' && this.zoomSourceBounds && this.zoomCenteredBounds;
        let requestedPanX = this.panX;
        let requestedPanY = this.panY;
        let panDamping = 1;
        if (desktopReturn) {
          const geometry = window.ScoreggiaMediaCore.desktopZoomReturnGeometry({
            scale: this.zoomScale,
            initialScale: this.zoomInitialScale,
            sourceBounds: this.zoomSourceBounds,
            viewportWidth: viewport.width,
            viewportHeight: viewport.height,
            viewportLeft: viewport.left,
            viewportTop: viewport.top,
            userPanX: this.panX,
            userPanY: this.panY,
          });
          if (!geometry) {
            this.resetZoom({ immediate: true, restoreFocus: true });
            return;
          }
          this.zoomBounds = geometry.bounds;
          this.zoomReturnProgress = geometry.returnProgress;
          requestedPanX = geometry.panX;
          requestedPanY = geometry.panY;
          panDamping = geometry.panDamping;
        } else {
          this.zoomBounds = this.zoomCenteredBounds || this.zoomBounds;
          this.zoomReturnProgress = 0;
        }
        if (!this.zoomBounds) return;
        this.updateZoomImageLayout();
        const atDesktopMinimum = desktopReturn && this.zoomScale <= this.zoomMinimumScale + 0.001;
        const panConstraints = {
          panX: atDesktopMinimum ? 0 : requestedPanX,
          panY: atDesktopMinimum ? 0 : requestedPanY,
          bounds: this.zoomBounds,
          scale: this.zoomScale,
          viewportWidth: viewport.width,
          viewportHeight: viewport.height,
          viewportLeft: viewport.left,
          viewportTop: viewport.top,
          strictBounds: true,
          allowFittedTravel: true,
        };
        if (desktopReturn && this.zoomScale <= this.zoomInitialScale) panConstraints.strictBounds = false;
        const constrained = window.ScoreggiaMediaCore.clampElevatedPan(panConstraints);
        this.zoomRenderedPanX = atDesktopMinimum ? 0 : constrained.x;
        this.zoomRenderedPanY = atDesktopMinimum ? 0 : constrained.y;
        if (desktopReturn) {
          if (atDesktopMinimum) {
            this.panX = 0;
            this.panY = 0;
          } else if (panDamping > 0.000001) {
            this.panX = constrained.x / panDamping;
            this.panY = constrained.y / panDamping;
          }
        } else {
          this.panX = constrained.x;
          this.panY = constrained.y;
        }
        if (immediate) {
          this.clearZoomTransformFrame();
          this.writeZoomTransform();
          return;
        }
        if (this.zoomTransformFrame !== null) return;
        this.zoomTransformFrame = window.requestAnimationFrame(() => {
          this.zoomTransformFrame = null;
          this.writeZoomTransform();
        });
      }

      writeZoomTransform() {
        if (!this.zoomImage || !this.zoomed) return;
        this.zoomImage.style.setProperty('--scoreggia-zoom-x', `${this.zoomRenderedPanX}px`);
        this.zoomImage.style.setProperty('--scoreggia-zoom-y', `${this.zoomRenderedPanY}px`);
        this.zoomImage.style.setProperty('--scoreggia-zoom-scale', String(this.zoomScale));
      }

      clearZoomTransformFrame() {
        if (this.zoomTransformFrame !== null) window.cancelAnimationFrame(this.zoomTransformFrame);
        this.zoomTransformFrame = null;
      }

      handleZoomDismissal(event) {
        if (!this.zoomed) return;
        event.preventDefault();
        event.stopPropagation();
        this.resetZoom({ restoreFocus: true });
      }

      handleZoomImageClick(event) {
        event.preventDefault();
        event.stopPropagation();
        if (this.suppressNextZoomClick) {
          this.clearZoomClickSuppression();
          return;
        }
        this.resetZoom({ restoreFocus: true });
      }

      handleZoomWheel(event) {
        if (!this.zoomed || !this.zoomBounds) return;
        const viewport = this.getZoomViewport();
        const deltaY = window.ScoreggiaMediaCore.normalizeWheelDelta({
          deltaY: event.deltaY,
          deltaMode: event.deltaMode,
          lineHeight: 16,
          pageHeight: viewport.height,
        });
        if (!deltaY) return;
        if (event.cancelable) event.preventDefault();
        event.stopPropagation();
        this.zoomImage?.classList.add('is-wheel-manipulating');
        if (this.zoomWheelIdleTimer !== null) {
          window.clearTimeout(this.zoomWheelIdleTimer);
          this.zoomWheelIdleTimer = null;
        }
        this.zoomWheelSamples.push({
          deltaY,
        });
        if (this.zoomWheelFrame !== null) return;
        this.zoomWheelFrame = window.requestAnimationFrame(() => {
          this.zoomWheelFrame = null;
          this.processZoomWheelSamples();
        });
      }

      processZoomWheelSamples() {
        if (!this.zoomed || !this.zoomBounds) {
          this.clearZoomWheelState();
          return;
        }
        if (!this.refreshZoomSourceGeometry()) return;
        this.zoomReturnMode = 'desktop-wheel';
        this.zoomWheelSamples.splice(0).forEach((sample) => {
          const nextScale = window.ScoreggiaMediaCore.wheelScaleFromDelta({
            scale: this.zoomScale,
            deltaY: sample.deltaY,
            minimumScale: this.zoomMinimumScale,
            maximumScale: this.zoomMaximumScale,
          });
          this.zoomScale = nextScale;
        });
        if (this.zoomScale <= this.zoomMinimumScale + 0.001) {
          this.zoomScale = this.zoomMinimumScale;
          if (!this.refreshZoomSourceGeometry()) return;
        }
        this.applyZoomTransform({ immediate: true });
        this.zoomWheelIdleTimer = window.setTimeout(() => {
          this.zoomWheelIdleTimer = null;
          this.zoomImage?.classList.remove('is-wheel-manipulating');
          if (this.zoomed && this.zoomScale <= this.zoomMinimumScale + 0.001) {
            this.zoomScale = this.zoomMinimumScale;
            this.resetZoom({ restoreFocus: true });
          }
        }, 160);
      }

      clearZoomWheelState() {
        if (this.zoomWheelFrame !== null) window.cancelAnimationFrame(this.zoomWheelFrame);
        if (this.zoomWheelIdleTimer !== null) window.clearTimeout(this.zoomWheelIdleTimer);
        this.zoomWheelFrame = null;
        this.zoomWheelIdleTimer = null;
        this.zoomWheelSamples = [];
        this.zoomImage?.classList.remove('is-wheel-manipulating');
      }

      handleZoomPointerDown(event) {
        if (!this.zoomed || (event.pointerType === 'mouse' && event.button !== 0)) return;
        if (this.zoomPointers.has(event.pointerId)) return;
        if (event.pointerType && event.pointerType !== 'mouse' && this.zoomReturnMode === 'desktop-wheel') {
          this.zoomReturnMode = null;
          this.zoomBounds = this.zoomCenteredBounds || this.zoomBounds;
          this.applyZoomTransform({ immediate: true });
        }
        if (event.cancelable) event.preventDefault();
        event.stopPropagation();
        if (this.zoomPointers.size === 0) {
          this.clearZoomClickSuppression();
          this.zoomManipulated = false;
        }
        const pointer = {
          id: event.pointerId,
          x: event.clientX,
          y: event.clientY,
          pointerType: event.pointerType || 'pointer',
          captured: false,
        };
        this.zoomPointers.set(event.pointerId, pointer);
        this.captureZoomPointer(pointer);
        this.bindZoomPointerListeners();
        this.restartZoomManipulation();
      }

      handleZoomPointerMove(event) {
        const pointer = this.zoomPointers.get(event.pointerId);
        if (!pointer || !this.zoomed) return;
        pointer.x = event.clientX;
        pointer.y = event.clientY;
        if (event.cancelable) event.preventDefault();

        if (this.zoomPointers.size >= 2) {
          const pinchPointers = this.zoomPinchPointerIds.map((id) => this.zoomPointers.get(id)).filter(Boolean);
          if (this.zoomMode !== 'pinch' || pinchPointers.length < 2) {
            this.beginZoomPinch();
            return;
          }
          const geometry = window.ScoreggiaMediaCore.pointerPairGeometry(pinchPointers);
          if (!geometry || !this.zoomPinchStart) return;
          const transform = window.ScoreggiaMediaCore.pinchTransformFromStart({
            startScale: this.zoomPinchStart.scale,
            startPanX: this.zoomPinchStart.panX,
            startPanY: this.zoomPinchStart.panY,
            startDistance: this.zoomPinchStart.distance,
            currentDistance: geometry.distance,
            startMidpoint: this.zoomPinchStart.midpoint,
            currentMidpoint: geometry.midpoint,
            center: {
              x: this.zoomBounds.left + this.zoomBounds.width / 2,
              y: this.zoomBounds.top + this.zoomBounds.height / 2,
            },
            minimumScale: this.zoomMinimumScale,
            maximumScale: this.zoomMaximumScale,
          });
          if (
            Math.abs(transform.scale - this.zoomPinchStart.scale) > 0.001 ||
            Math.hypot(
              geometry.midpoint.x - this.zoomPinchStart.midpoint.x,
              geometry.midpoint.y - this.zoomPinchStart.midpoint.y,
            ) > 0.5
          ) {
            this.markZoomManipulated();
          }
          this.zoomScale = transform.scale;
          this.panX = transform.x;
          this.panY = transform.y;
          this.applyZoomTransform();
          return;
        }

        if (this.zoomMode !== 'pan' || !this.zoomPanStart) this.beginZoomPan(pointer);
        const deltaX = pointer.x - this.zoomPanStart.pointerX;
        const deltaY = pointer.y - this.zoomPanStart.pointerY;
        if (Math.hypot(deltaX, deltaY) > 5) this.markZoomManipulated();
        this.panX = this.zoomPanStart.panX + deltaX;
        this.panY = this.zoomPanStart.panY + deltaY;
        this.applyZoomTransform();
      }

      handleZoomPointerEnd(event) {
        this.finishZoomPointer(event.pointerId, false);
      }

      handleZoomPointerCancel(event) {
        this.finishZoomPointer(event.pointerId, true);
      }

      finishZoomPointer(pointerId, canceled) {
        const pointer = this.zoomPointers.get(pointerId);
        if (!pointer) return;
        const completedPinch = this.zoomMode === 'pinch';
        this.releaseZoomPointer(pointer);
        this.zoomPointers.delete(pointerId);
        if (completedPinch && this.zoomScale <= this.zoomMinimumScale + 0.001) {
          this.zoomScale = this.zoomMinimumScale;
          this.applyZoomTransform({ immediate: true });
          this.resetZoom({ restoreFocus: true });
          return;
        }
        if (this.zoomPointers.size > 0) {
          this.restartZoomManipulation();
          return;
        }
        this.clearZoomPointerListeners();
        this.zoomMode = null;
        this.zoomPanStart = null;
        this.zoomPinchStart = null;
        this.zoomPinchPointerIds = [];
        this.zoomImage?.classList.remove('is-manipulating');
        if (this.zoomManipulated) this.markZoomClickForSuppression();
        this.zoomManipulated = false;
        if (
          canceled &&
          ![this.zoomScale, this.panX, this.panY].every(Number.isFinite)
        ) {
          this.resetZoom({ immediate: true, restoreFocus: true });
        }
      }

      restartZoomManipulation() {
        const pointers = Array.from(this.zoomPointers.values());
        if (pointers.length >= 2) this.beginZoomPinch();
        else if (pointers.length === 1) this.beginZoomPan(pointers[0]);
        else {
          this.zoomMode = null;
          this.zoomPanStart = null;
          this.zoomPinchStart = null;
          this.zoomPinchPointerIds = [];
        }
      }

      beginZoomPan(pointer) {
        if (!pointer) return;
        this.zoomMode = 'pan';
        this.zoomPinchStart = null;
        this.zoomPinchPointerIds = [];
        this.zoomPanStart = {
          pointerX: pointer.x,
          pointerY: pointer.y,
          panX: this.panX,
          panY: this.panY,
        };
      }

      beginZoomPinch() {
        const pointers = Array.from(this.zoomPointers.values()).slice(0, 2);
        const geometry = window.ScoreggiaMediaCore.pointerPairGeometry(pointers);
        if (!geometry || geometry.distance <= 0) return;
        this.zoomMode = 'pinch';
        this.zoomPanStart = null;
        this.zoomPinchPointerIds = pointers.map((pointer) => pointer.id);
        this.zoomPinchStart = {
          distance: geometry.distance,
          midpoint: geometry.midpoint,
          scale: this.zoomScale,
          panX: this.panX,
          panY: this.panY,
        };
      }

      markZoomManipulated() {
        this.zoomManipulated = true;
        this.zoomImage?.classList.add('is-manipulating');
      }

      captureZoomPointer(pointer) {
        if (!pointer || !this.zoomImage?.setPointerCapture) return;
        try {
          this.zoomImage.setPointerCapture(pointer.id);
          pointer.captured = this.zoomImage.hasPointerCapture ? this.zoomImage.hasPointerCapture(pointer.id) : true;
        } catch {
          pointer.captured = false;
        }
      }

      releaseZoomPointer(pointer) {
        if (!pointer?.captured) return;
        if (!this.zoomImage?.releasePointerCapture) {
          pointer.captured = false;
          return;
        }
        let captured = pointer.captured;
        if (this.zoomImage.hasPointerCapture) {
          try {
            captured = this.zoomImage.hasPointerCapture(pointer.id);
          } catch {
            captured = false;
          }
        }
        if (!captured) {
          pointer.captured = false;
          return;
        }
        pointer.captured = false;
        try {
          this.zoomImage.releasePointerCapture(pointer.id);
        } catch {
          return;
        }
      }

      bindZoomPointerListeners() {
        if (this.activeZoomPointerListeners) return;
        const move = (event) => this.handleZoomPointerMove(event);
        const end = (event) => this.handleZoomPointerEnd(event);
        const cancel = (event) => this.handleZoomPointerCancel(event);
        window.addEventListener('pointermove', move, { passive: false });
        window.addEventListener('pointerup', end);
        window.addEventListener('pointercancel', cancel);
        this.activeZoomPointerListeners = { move, end, cancel };
      }

      clearZoomPointerListeners() {
        const listeners = this.activeZoomPointerListeners;
        if (!listeners) return;
        window.removeEventListener('pointermove', listeners.move);
        window.removeEventListener('pointerup', listeners.end);
        window.removeEventListener('pointercancel', listeners.cancel);
        this.activeZoomPointerListeners = null;
      }

      markZoomClickForSuppression() {
        this.clearZoomClickSuppression();
        this.suppressNextZoomClick = true;
        this.zoomClickTimer = window.setTimeout(() => this.clearZoomClickSuppression(), 500);
      }

      clearZoomClickSuppression() {
        if (this.zoomClickTimer !== null) window.clearTimeout(this.zoomClickTimer);
        this.zoomClickTimer = null;
        this.suppressNextZoomClick = false;
      }

      cancelZoomManipulation() {
        this.zoomPointers.forEach((pointer) => this.releaseZoomPointer(pointer));
        this.zoomPointers.clear();
        this.clearZoomPointerListeners();
        this.zoomMode = null;
        this.zoomPanStart = null;
        this.zoomPinchStart = null;
        this.zoomPinchPointerIds = [];
        this.zoomManipulated = false;
        this.zoomImage?.classList.remove('is-manipulating');
      }

      get activeMediaBusy() {
        const activeSlide = this.controller?.slides[this.controller.activeIndex];
        if (!activeSlide) return false;
        const playingVideo = Array.from(activeSlide.querySelectorAll('video')).some((video) => !video.paused);
        return (
          playingVideo ||
          Boolean(activeSlide.querySelector('deferred-media[loaded], product-model[loaded], iframe:focus, model-viewer:focus'))
        );
      }
    },
  );
}
